import type { ChartSpec, Question } from "./types";

/** The player's answer: an option id, or a number (typed or a clicked price). */
export type Answer = string | number;

export function isCorrect(q: Question, a: Answer): boolean {
  switch (q.kind) {
    case "choice":
      return a === q.answer;
    case "number":
      return typeof a === "number" && Number.isFinite(a) && Math.abs(a - q.answer) <= q.tolerance + 1e-9 * Math.max(1, Math.abs(q.answer));
    case "price": {
      if (typeof a !== "number" || !Number.isFinite(a)) return false;
      const lo = Math.min(q.accept.from, q.accept.to);
      const hi = Math.max(q.accept.from, q.accept.to);
      return a >= lo && a <= hi;
    }
  }
}

const BAD_TEXT = /\b(NaN|undefined|null|Infinity)\b|\[object/;

function textProblems(where: string, s: string | undefined, required: boolean): string[] {
  if (s === undefined) return required ? [`${where} is missing`] : [];
  if (typeof s !== "string") return [`${where} is not text`];
  if (required && s.trim().length === 0) return [`${where} is empty`];
  if (BAD_TEXT.test(s)) return [`${where} shows a broken value: "${s}"`];
  return [];
}

function chartProblems(c: ChartSpec): string[] {
  const out: string[] = [];
  if (c.candles.length < 10) out.push(`chart has only ${c.candles.length} candles`);
  const all = [...c.candles, ...(c.ahead ?? [])];
  for (let i = 0; i < all.length; i++) {
    const k = all[i]!;
    if (![k.time, k.open, k.high, k.low, k.close, k.volume].every(Number.isFinite)) { out.push(`candle ${i} has a non-number`); break; }
    if (k.high < Math.max(k.open, k.close) - 1e-12 || k.low > Math.min(k.open, k.close) + 1e-12) { out.push(`candle ${i} high/low don't contain open/close`); break; }
    if (i > 0 && k.time <= all[i - 1]!.time) { out.push(`candle ${i} is out of order`); break; }
  }
  const times = new Set(c.candles.map(k => k.time));
  const aheadTimes = new Set((c.ahead ?? []).map(k => k.time));
  for (const m of c.marks ?? []) if (!times.has(m.time)) out.push(`mark "${m.label}" points at a candle the player can't see`);
  for (const m of c.answerMarks ?? []) if (!times.has(m.time) && !aheadTimes.has(m.time)) out.push(`answer mark "${m.label}" points at no candle`);
  for (const l of [...(c.lines ?? []), ...(c.answerLines ?? [])]) {
    if (!Number.isFinite(l.price)) out.push(`line "${l.label}" has no price`);
    out.push(...textProblems(`line label`, l.label, true));
  }
  for (const z of [...(c.zones ?? []), ...(c.answerZones ?? [])]) {
    if (!Number.isFinite(z.from) || !Number.isFinite(z.to)) out.push("a zone has no price");
  }
  return out;
}

/** What's wrong with a question (empty when it's fine): the games' shared quality bar. */
export function questionProblems(q: Question): string[] {
  const out: string[] = [];
  out.push(...textProblems("prompt", q.prompt, true));
  out.push(...textProblems("explain", q.explain, true));
  out.push(...textProblems("outcome", q.outcome, false));
  for (const f of q.facts ?? []) {
    out.push(...textProblems(`fact label`, f.label, true));
    out.push(...textProblems(`fact "${f.label}"`, f.value, true));
  }
  if (q.chart) out.push(...chartProblems(q.chart));
  switch (q.kind) {
    case "choice": {
      if (q.options.length < 2 || q.options.length > 5) out.push(`${q.options.length} options (2–5 allowed)`);
      const ids = new Set(q.options.map(o => o.id));
      if (ids.size !== q.options.length) out.push("option ids repeat");
      const labels = new Set(q.options.map(o => o.label));
      if (labels.size !== q.options.length) out.push(`option labels repeat: ${q.options.map(o => o.label).join(" | ")}`);
      if (!ids.has(q.answer)) out.push(`answer "${q.answer}" is not an option`);
      for (const o of q.options) {
        out.push(...textProblems("option label", o.label, true));
        out.push(...textProblems("option why", o.why, false));
      }
      break;
    }
    case "number":
      if (!Number.isFinite(q.answer)) out.push("answer is not a number");
      if (!(q.tolerance >= 0) || !Number.isFinite(q.tolerance)) out.push("tolerance must be ≥ 0");
      if (!Number.isInteger(q.decimals) || q.decimals < 0 || q.decimals > 8) out.push("decimals must be 0–8");
      // The answer as shown must itself be accepted.
      if (Number.isFinite(q.answer) && !isCorrect(q, Number(q.answer.toFixed(q.decimals)))) out.push(`the answer rounded to ${q.decimals} decimals falls outside the tolerance`);
      break;
    case "price": {
      if (!q.chart) out.push("a price question needs a chart");
      if (!Number.isFinite(q.accept.from) || !Number.isFinite(q.accept.to)) out.push("accept range has no price");
      if (!isCorrect(q, q.answer)) out.push("the ideal price is outside the accepted range");
      if (q.chart) {
        const lo = Math.min(...q.chart.candles.map(c => c.low));
        const hi = Math.max(...q.chart.candles.map(c => c.high));
        const pad = (hi - lo) * 0.5;
        if (q.answer < lo - pad || q.answer > hi + pad) out.push("the ideal price is far off the chart");
      }
      break;
    }
  }
  return out;
}
